import React, { useState, useEffect } from "react";
import SEO from "../seo";
const StoreOperation = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["overview", "get-started", "key-benefits"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Store Operation | xPage Help Center"
        description="Learn how to manage products on xPage. Add physical or digital products, import in bulk, use dropshipping integrations, and track inventory efficiently for multi-channel sales."
        keywords="xPage, product management, add products, digital products, physical products, inventory tracking, dropshipping, product collections, multi-channel sales"
        canonical="https://help.xpage.ai/help/store-operation/store-operation"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Products
          </h1>
          <p className="text-sm text-systemLabel">
            xPage provides a powerful product management system that makes it
            simple to sell both physical and digital products. You can quickly
            add products by entering details, uploading images, and setting
            pricing, allowing you to start selling in minutes. Whether your
            items have multiple variants (like size and color) or a single
            configuration, xPage's flexible variant management ensures accurate
            inventory control and smooth sales operations.
          </p>
        </header>

        {/* Get Started Section */}
        <section id="get-started" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Get started
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When you first visit the Products page in your xPage admin, you'll
              find quick options to help you start building your product
              catalog:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Option</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Add physical product
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Create a product that requires shipping. You can set
                    variants, inventory, shipping rates, and more.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Add digital product
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Offer downloadable content such as eBooks, courses, or
                    software.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Import products
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Bulk add products using CSV import to save time when setting
                    up or updating your store.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Dropshipping integrations
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Connect with supported dropshipping services to
                    automatically sync products from suppliers.
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="text-sm text-systemLabel">
              Click any option to begin creating your products. The system will
              guide you step-by-step through entering product details, setting
              variants, adding images, and configuring pricing.
            </p>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section id="key-benefits" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Key benefits
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Benefit
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Multi-channel sales support
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Publish your products to multiple sales channels (including
                    integrated platforms) in just one click, increasing
                    visibility and reach.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Flexible variant management
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Add multiple specifications for a single product (such as
                    size, material, and color) and link them directly to
                    inventory for precise tracking.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Real-time inventory tracking
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    xPage automatically updates stock levels in real time,
                    helping you avoid overselling or running out of inventory.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Product collections
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Organize your store with manual or automated collections to
                    help customers browse and find what they need more
                    efficiently.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#get-started"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "get-started"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Get Started
              </a>
            </li>
            <li>
              <a
                href="#key-benefits"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "key-benefits"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Key Benefits
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StoreOperation;
